/**
 * Predefined endcard video options for the veo_shorts_v1 template.
 *
 * Assets live in Firebase Storage under language-partitioned paths:
 *
 *   Preview (low-res MP4, for UI preview only):
 *     endcards/<language>/proxy/endcard_video-{N}.mp4
 *
 *   Render (original MOV, for Remotion render input):
 *     endcards/<language>/original/endcard_video-{N}.mov
 *
 * The download URL pattern used here is the standard Firebase Storage REST URL:
 *   https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded-path}?alt=media
 */

import { app } from './firebase';

/** The storage bucket name is read from the Firebase app config — always correct for the current environment. */
const STORAGE_BUCKET = app.options.storageBucket ?? '';

/** Builds a Firebase Storage download URL for the given storage path. */
function buildStorageUrl(storagePath: string): string {
  return `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/${encodeURIComponent(storagePath)}?alt=media`;
}

/** Low-res MP4 proxy URL for frontend preview. */
function proxyUrl(n: number, lang = 'en'): string {
  return buildStorageUrl(`endcards/${lang}/proxy/endcard_video-${n}.mp4`);
}

/** Original MOV URL for Remotion render input. */
function originalUrl(n: number, lang = 'en'): string {
  return buildStorageUrl(`endcards/${lang}/original/endcard_video-${n}.mov`);
}

export interface EndcardOption {
  id: string;
  label: string;
  /** Low-res MP4 proxy URL used for frontend preview only. */
  previewUrl: string;
  /** Original MOV URL used as Remotion render input. */
  videoUrl: string;
}

/**
 * The six predefined endcard options available in the Scene 5 — End Frame dropdown.
 * These are always shown for the endCardVideo slot regardless of whether the template
 * document in Firestore includes its own endCardOptions array.
 */
export const ENDCARD_OPTIONS: EndcardOption[] = [
  { id: 'endcard_video-1', label: 'Endcard 1', previewUrl: proxyUrl(1), videoUrl: originalUrl(1) },
  { id: 'endcard_video-2', label: 'Endcard 2', previewUrl: proxyUrl(2), videoUrl: originalUrl(2) },
  { id: 'endcard_video-3', label: 'Endcard 3', previewUrl: proxyUrl(3), videoUrl: originalUrl(3) },
  { id: 'endcard_video-4', label: 'Endcard 4', previewUrl: proxyUrl(4), videoUrl: originalUrl(4) },
  { id: 'endcard_video-5', label: 'Endcard 5', previewUrl: proxyUrl(5), videoUrl: originalUrl(5) },
  { id: 'endcard_video-6', label: 'Endcard 6', previewUrl: proxyUrl(6), videoUrl: originalUrl(6) },
];
